import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Página pública de recuperación para usuarios con datos corruptos en el navegador.
 * Caso típico: la misma cuenta funciona en otro navegador / dispositivo pero falla
 * en uno específico (típicamente Chrome de oficina con extensions o cache antiguo).
 *
 * URL pública: /recover
 * - Limpia localStorage, sessionStorage y cookies del dominio.
 * - Intenta desregistrar service workers si los hay.
 * - Redirige al login al terminar.
 */
export default function Recover() {
    const [status, setStatus] = useState('idle'); // idle | working | done | error
    const [details, setDetails] = useState([]);

    useEffect(() => {
        try {
            const ua = navigator.userAgent;
            const isChrome = /Chrome\/(\d+)/.test(ua) && !/Edg\//.test(ua);
            const m = ua.match(/Chrome\/(\d+)/);
            setDetails([
                `Navegador detectado: ${isChrome ? `Google Chrome ${m ? m[1] : ''}` : 'Otro'}`,
                `Modo incógnito: no detectable directamente`,
            ]);
        } catch (_) { /* silent */ }
    }, []);

    const handleReset = async () => {
        setStatus('working');
        const steps = [];
        try {
            window.localStorage.clear();
            steps.push('✓ localStorage limpiado');
        } catch (_) {
            steps.push('✗ localStorage no se pudo limpiar');
        }
        try {
            window.sessionStorage.clear();
            steps.push('✓ sessionStorage limpiado');
        } catch (_) {
            steps.push('✗ sessionStorage no se pudo limpiar');
        }
        try {
            let cookieCount = 0;
            document.cookie.split(';').forEach((c) => {
                const eq = c.indexOf('=');
                const name = (eq > -1 ? c.substr(0, eq) : c).trim();
                if (name) {
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                    cookieCount += 1;
                }
            });
            steps.push(`✓ ${cookieCount} cookie(s) eliminada(s) (HttpOnly excluidas)`);
        } catch (_) {
            steps.push('✗ Cookies no se pudieron limpiar');
        }
        try {
            if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                for (const r of regs) {
                    await r.unregister();
                }
                steps.push(`✓ ${regs.length} service worker(s) desregistrado(s)`);
            }
        } catch (_) {
            steps.push('✗ Service workers no se pudieron limpiar');
        }
        try {
            if (window.caches && window.caches.keys) {
                const keys = await window.caches.keys();
                await Promise.all(keys.map((k) => window.caches.delete(k)));
                steps.push(`✓ ${keys.length} caché(s) del navegador eliminada(s)`);
            }
        } catch (_) {
            steps.push('✗ Caché del navegador no se pudo limpiar');
        }
        setDetails((prev) => [...prev, ...steps]);
        setStatus('done');
        setTimeout(() => {
            window.location.replace('/login');
        }, 2200);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
            <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                        <AlertTriangle className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                        <h1 className="font-heading text-lg font-bold text-slate-900" data-testid="recover-title">
                            Reparar mi sesión
                        </h1>
                        <p className="text-xs text-slate-500">
                            Útil si la plataforma falla solo en este navegador
                        </p>
                    </div>
                </div>

                <p className="text-sm text-slate-700 leading-relaxed">
                    Si la plataforma <strong>funciona en tu celular o en otro navegador</strong> pero
                    falla aquí (típicamente Chrome con caché antigua o extensiones), este botón limpiará
                    los datos locales para que vuelvas a entrar limpio.
                </p>

                <ul className="mt-3 text-xs text-slate-500 list-disc list-inside space-y-0.5">
                    <li>Tu trabajo guardado <strong>NO se perderá</strong> — está en nuestra base de datos.</li>
                    <li>Tendrás que iniciar sesión nuevamente.</li>
                    <li>Las extensiones del navegador no se modifican (revisa AdBlock o similares).</li>
                </ul>

                {details.length > 0 && (
                    <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
                        <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider mb-1">
                            Diagnóstico
                        </p>
                        <ul className="text-[11px] text-slate-700 space-y-0.5 font-mono">
                            {details.map((d, i) => <li key={i}>{d}</li>)}
                        </ul>
                    </div>
                )}

                <div className="mt-5 flex flex-wrap gap-2">
                    {status === 'idle' && (
                        <button
                            type="button"
                            onClick={handleReset}
                            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 transition-colors"
                            data-testid="recover-reset-btn"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Limpiar datos y volver a entrar
                        </button>
                    )}
                    {status === 'working' && (
                        <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Limpiando datos del navegador…
                        </div>
                    )}
                    {status === 'done' && (
                        <div className="inline-flex items-center gap-2 text-sm text-emerald-700 font-medium" data-testid="recover-done">
                            <CheckCircle2 className="h-4 w-4" />
                            ¡Listo! Redirigiendo al inicio de sesión…
                        </div>
                    )}
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                        Volver a inicio de sesión
                    </Link>
                </div>

                <p className="mt-4 text-[10px] text-slate-400 leading-relaxed">
                    ¿Sigues con problemas después de esto? Prueba en <strong>modo incógnito</strong> (Ctrl+Shift+N).
                    Si funciona ahí, el problema es una extensión del navegador. Si tampoco funciona, escríbenos a soporte.
                </p>
            </div>
        </div>
    );
}
